
import { 
    collection, getDocs, doc, getDoc, setDoc, updateDoc, deleteDoc, 
    query, where, serverTimestamp, addDoc, orderBy, DocumentSnapshot
} from 'firebase/firestore';
import { initializeApp, deleteApp } from 'firebase/app';
import { getAuth, createUserWithEmailAndPassword } from 'firebase/auth';
import { db, firebaseConfig } from './firebase';
import { User, Tenant, Company, Filter, Video, AiAnalysis, KpiDefinition } from '../types';

const fromDocWithId = (docSnapshot: DocumentSnapshot, idField: string) => {
    const data = docSnapshot.data();
    return data ? { ...data, [idField]: docSnapshot.id } : null;
};

const cleanData = (obj: any) => {
  const newObj = { ...obj };
  Object.keys(newObj).forEach(key => {
    if (newObj[key] === undefined) {
      delete newObj[key];
    }
  });
  return newObj;
};

export const firestoreService = {
  getTenants: async (): Promise<Tenant[]> => {
    const q = query(collection(db, 'tenants'), orderBy('createdAt', 'desc'));
    const snapshot = await getDocs(q);
    return snapshot.docs.map(d => fromDocWithId(d, 'tenantId') as any as Tenant);
  },

  getTenant: async (tenantId: string): Promise<Tenant | null> => {
    const docRef = doc(db, 'tenants', tenantId);
    const docSnap = await getDoc(docRef);
    return docSnap.exists() ? fromDocWithId(docSnap, 'tenantId') as any as Tenant : null;
  },

  addTenant: async (data: Omit<Tenant, 'tenantId' | 'createdAt'>): Promise<Tenant> => {
    const docRef = await addDoc(collection(db, 'tenants'), {
      ...data,
      createdAt: serverTimestamp(),
    });
    return { ...data, tenantId: docRef.id, createdAt: serverTimestamp() } as any as Tenant;
  },

  updateTenant: async (tenantId: string, data: any): Promise<void> => {
    await updateDoc(doc(db, 'tenants', tenantId), cleanData(data));
  },

  deleteTenant: async (tenantId: string): Promise<void> => {
    await deleteDoc(doc(db, 'tenants', tenantId));
  },

  getCompanies: async (tenantId: string): Promise<Company[]> => {
    const q = query(collection(db, 'companies'), where('tenantId', '==', tenantId));
    const snapshot = await getDocs(q);
    return snapshot.docs.map(d => fromDocWithId(d, 'companyId') as any as Company);
  },

  getAllCompanies: async (): Promise<Company[]> => {
    const snapshot = await getDocs(collection(db, 'companies'));
    return snapshot.docs.map(d => fromDocWithId(d, 'companyId') as any as Company);
  },

  getCompany: async (companyId: string): Promise<Company | null> => {
    const docRef = doc(db, 'companies', companyId);
    const docSnap = await getDoc(docRef);
    return docSnap.exists() ? fromDocWithId(docSnap, 'companyId') as any as Company : null;
  },

  addCompany: async (data: Omit<Company, 'companyId' | 'createdAt'>): Promise<Company> => {
    const docRef = await addDoc(collection(db, 'companies'), {
      ...data,
      createdAt: serverTimestamp(),
    });
    return { ...data, companyId: docRef.id, createdAt: serverTimestamp() } as any as Company;
  },

  updateCompany: async (companyId: string, data: any): Promise<void> => {
    await updateDoc(doc(db, 'companies', companyId), cleanData(data));
  },

  deleteCompany: async (companyId: string): Promise<void> => {
    await deleteDoc(doc(db, 'companies', companyId));
  },

  registerUser: async (userData: any, password?: string): Promise<User> => {
    const secondaryAppName = `secondary-app-${Date.now()}`;
    const secondaryApp = initializeApp(firebaseConfig, secondaryAppName);
    const secondaryAuth = getAuth(secondaryApp);

    try {
      const userCredential = await createUserWithEmailAndPassword(secondaryAuth, userData.email, password!);
      const uid = userCredential.user.uid;
      const userRef = doc(db, "users", uid);
      const cleaned = cleanData({ ...userData, uid, createdAt: serverTimestamp() });
      await setDoc(userRef, cleaned);
      await deleteApp(secondaryApp);
      return { ...userData, uid, createdAt: serverTimestamp() } as any as User;
    } catch (error) {
      await deleteApp(secondaryApp);
      throw error;
    }
  },

  getUserProfile: async (uid: string): Promise<User | null> => {
    const userDocRef = doc(db, 'users', uid);
    const userDoc = await getDoc(userDocRef);
    return userDoc.exists() ? fromDocWithId(userDoc, 'uid') as any as User : null;
  },

  getUsers: async (tenantId: string): Promise<User[]> => {
    const q = query(collection(db, 'users'), where('tenantId', '==', tenantId));
    const snapshot = await getDocs(q);
    return snapshot.docs.map(d => fromDocWithId(d, 'uid') as any as User);
  },

  getAllUsers: async (): Promise<User[]> => {
    const q = query(collection(db, 'users'), orderBy('createdAt', 'desc'));
    const snapshot = await getDocs(q);
    return snapshot.docs.map(d => fromDocWithId(d, 'uid') as any as User);
  },

  updateUser: async (uid: string, data: any): Promise<void> => {
    await updateDoc(doc(db, 'users', uid), cleanData(data));
  },

  deleteUser: async (uid: string): Promise<void> => {
    await deleteDoc(doc(db, 'users', uid));
  },
  
  getFilters: async (tenantId: string, companyId: string): Promise<Filter[]> => {
    const q = query(
      collection(db, 'filters'), 
      where('tenantId', '==', tenantId), 
      where('companyId', '==', companyId)
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(d => fromDocWithId(d, 'filterId') as any as Filter);
  },

  addFilter: async (data: Omit<Filter, 'filterId' | 'createdAt'>): Promise<Filter> => {
    const docRef = await addDoc(collection(db, 'filters'), {
      ...data,
      createdAt: serverTimestamp(),
    });
    return { ...data, filterId: docRef.id, createdAt: serverTimestamp() } as any as Filter;
  },

  updateFilter: async (filterId: string, data: any): Promise<void> => {
    await updateDoc(doc(db, 'filters', filterId), cleanData(data));
  },

  deleteFilter: async (filterId: string): Promise<void> => {
    await deleteDoc(doc(db, 'filters', filterId));
  },

  getVideos: async (tenantId: string, companyId: string): Promise<Video[]> => {
    const q = query(
      collection(db, 'videos'), 
      where('tenantId', '==', tenantId),
      where('companyId', '==', companyId)
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(d => fromDocWithId(d, 'videoId') as any as Video);
  },

  getVideo: async (videoId: string): Promise<Video | null> => {
    const docRef = doc(db, 'videos', videoId);
    const docSnap = await getDoc(docRef);
    return docSnap.exists() ? fromDocWithId(docSnap, 'videoId') as any as Video : null;
  },

  addVideo: async (data: Omit<Video, 'videoId' | 'createdAt'>): Promise<Video> => {
    const docRef = await addDoc(collection(db, 'videos'), {
      ...data,
      createdAt: serverTimestamp(),
    });
    return { ...data, videoId: docRef.id, createdAt: serverTimestamp() } as any as Video;
  },

  deleteVideo: async (videoId: string): Promise<void> => {
    await deleteDoc(doc(db, 'videos', videoId));
  },

  getAnalysis: async (analysisId: string): Promise<AiAnalysis | null> => {
    const docRef = doc(db, 'analyses', analysisId);
    const docSnap = await getDoc(docRef);
    return docSnap.exists() ? fromDocWithId(docSnap, 'analysisId') as any as AiAnalysis : null;
  },

  saveAnalysis: async (tenantId: string, data: any, analysisId?: string): Promise<void> => {
    const cleaned = cleanData(data);
    if (analysisId) {
      await updateDoc(doc(db, 'analyses', analysisId), cleaned);
    } else {
      const docRef = await addDoc(collection(db, 'analyses'), {
        ...cleaned,
        generatedAt: serverTimestamp(),
      });
      await updateDoc(doc(db, 'videos', data.videoId), {
        aiAnalysisId: docRef.id,
        status: 'ready'
      });
    }
  },

  getCompanyKpiProfile: async (companyId: string): Promise<any> => {
    const q = query(collection(db, 'companyKpiProfiles'), where('companyId', '==', companyId));
    const snapshot = await getDocs(q);
    if (snapshot.empty) return null;
    return fromDocWithId(snapshot.docs[0], 'profileId');
  },

  saveCompanyKpiProfile: async (tenantId: string, companyId: string, kpis: KpiDefinition[]): Promise<void> => {
    const q = query(collection(db, 'companyKpiProfiles'), where('companyId', '==', companyId));
    const snapshot = await getDocs(q);
    const data = { tenantId, companyId, kpis, updatedAt: serverTimestamp() };

    if (snapshot.empty) {
      await addDoc(collection(db, 'companyKpiProfiles'), data);
    } else {
      await updateDoc(doc(db, 'companyKpiProfiles', snapshot.docs[0].id), data);
    }
  }
};
